import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { Mail, Send, CheckCircle2, MessageSquare } from "lucide-react";

const ContactUs = ({ auth }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: auth.user?.name || "",
    email: auth.user?.email || "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <DashboardLayout auth={auth}>
        <div className="max-w-2xl mx-auto" data-testid="contact-success">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">
                {t('contact.successTitle')}
              </h2>
              <p className="text-slate-600 mb-6">
                {t('contact.successMessage')}
              </p>
              <Button 
                onClick={() => {
                  setSubmitted(false);
                  setFormData({ name: auth.user?.name || "", email: auth.user?.email || "", subject: "", message: "" });
                }}
                variant="outline"
              >
                Send Another Message
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout auth={auth}>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="contact-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">{t('contact.title')}</h1>
          <p className="text-slate-600">{t('contact.subtitle')}</p>
        </div>

        {/* Contact Form */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b bg-gradient-to-r from-maple-50 to-red-50">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-maple" />
              {t('contact.sendMessage')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('contact.yourName')}</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    data-testid="contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('contact.emailAddress')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">{t('contact.subject')}</Label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What is this regarding?"
                  data-testid="contact-subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">{t('contact.message')}</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us how we can help you..."
                  rows={5}
                  data-testid="contact-message"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-maple hover:bg-maple-600 text-white"
                disabled={loading}
                data-testid="contact-submit"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t('contact.sending')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('contact.send')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-truenorth-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-truenorth" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-slate-900 mb-1">{t('contact.responseTime')}</h3>
                <p className="text-sm text-slate-600">
                  {t('contact.responseInfo')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ContactUs;
